import { inject, Injectable, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { Firestore, collection, onSnapshot } from '@angular/fire/firestore';
import { AuthService } from './core/auth/auth.service';
import { RdUser } from './core/user/user.types';
import { distinctUntilChanged } from 'rxjs/operators';
import { User } from 'firebase/auth';

@Injectable({ providedIn: 'root' })
export class AppService {

    private authService = inject(AuthService);
    private firestore = inject(Firestore);
    private injector = inject(EnvironmentInjector);

    userProfilesSubject = new BehaviorSubject<RdUser[] | null>(null);
    userProfiles$ = this.userProfilesSubject.asObservable();

    private unsubscribeSnapshot?: () => void;
    private authContextSubscription?: Subscription;

    constructor() {
        this.initializeAuthListeners();
    }

    private initializeAuthListeners(): void {
        this.initializeAuthContextListener();

        this.authService.onSignOut.subscribe(() => {
            this.handleSignOut();
        });
    }

    private initializeAuthContextListener(): void {
        this.authContextSubscription?.unsubscribe();

        this.authContextSubscription = combineLatest([
            this.authService.orgId$,
            this.authService.firebaseUser$
        ])
            .pipe(
                distinctUntilChanged(([previousOrgId, previousUser], [nextOrgId, nextUser]) =>
                    previousOrgId === nextOrgId && previousUser?.uid === nextUser?.uid
                )
            )
            .subscribe(([orgId, firebaseUser]) => this.handleAuthContextChange(orgId, firebaseUser));
    }

    private handleAuthContextChange(orgId: string | null, firebaseUser: User | null): void {
        this.cleanup();

        if (!orgId || !firebaseUser) {
            this.userProfilesSubject.next(null);
            return;
        }

        if (!this.authService.getIsExternal()) {
            this.subscribeToUserProfiles(orgId);
        }
    }

    private subscribeToUserProfiles(orgId: string): void {
        runInInjectionContext(this.injector, () => {
            const ref = collection(
                this.firestore,
                `organisations/${orgId}/userProfiles`
            );

            this.unsubscribeSnapshot = onSnapshot(
                ref,
                snapshot => this.handleUserProfilesSnapshot(snapshot),
                error => this.handleUserProfilesError(error)
            );
        });
    }

    private handleUserProfilesSnapshot(snapshot: any): void {
        const users = snapshot.docs.map(doc => ({
            id: doc.id,
            ...(doc.data() as RdUser)
        }));

        this.userProfilesSubject.next(users);
    }

    private handleUserProfilesError(error: any): void {
        console.error('Error fetching user profiles:', error);
        this.userProfilesSubject.next(null);
    }

    

    private handleSignOut(): void {
        this.userProfilesSubject.next(null);
        this.cleanup();
    }

    private cleanup(): void {
        if (this.unsubscribeSnapshot) {
            this.unsubscribeSnapshot();
            this.unsubscribeSnapshot = undefined;
        }
    }

    ngOnDestroy(): void {
        this.authContextSubscription?.unsubscribe();
        this.cleanup();
    }
}
